import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Heading,
  CheckBox,
  Select,
  FormField,
  TextInput,
  Text,
} from "grommet";
import HotelService from "../../services/hotel.service";
import { presentToastSuccess, presentToastErrorContent } from "@shared/Toast";
import { presentAlertPrompt } from "@shared/Alert";
import { useFetchHotel } from "./redux/hooks";
import {
  LANGUAGE_OPTIONS_LIST,
  LOCK_TYPE_OPTIONS_LIST,
  PRONOUNS_FR_OPTIONS_LIST,
} from "@common/utils";
import moment from "moment";
import { pmsThatCanClearPmsUpdated } from "@common/features";
import { useTranslation } from "react-i18next";

import { getOfferCapabilities, LOCK_FIELDS, changedVisibleFields } from "../../common/offerCapabilities";

const adminDefaultValues = {
  serviceType: "access-doors",
  upsellsEnabled: false,
  messagingEnabled: false,
  disabled: false,
  language: "en",
  pronounsFr: "LE",
  sendMobileKeyQuietly: false,
  demo: false,
  lockType: "ttlock",
  messagingDisabled: false,
  useUpgradeModules: false,
  disableBatteryCheck: false,
  adminPin: undefined,
  renewSubscription: undefined,
  aiEnabled: false,
  aiInsightsEnabled: false,
  hasWifiGateway: false,
  usePasscodes: false,
  useRandomCode: false,
  numberOfLastDigitsUsed: false,
  partialSetup: false,
  partialSetupStrict: false,
  houseCleaningEnabled: false,
  optOutMarketingOptionEnabled: true,
  pmsId: "",

  commonAreaFixedPasscode: "",
  commonAreaFixedLabel: null,
  sector: "2",
  disableAllCommunications: false,
  subscriptionExpiration: "",
  whiteLabeled: false,
};

const initialValues = hotel => ({ ...adminDefaultValues, ...hotel,
  serviceType: getOfferCapabilities(hotel).serviceType,
  upsellsEnabled: getOfferCapabilities(hotel).upsells });

export default function SettingsAdmin() {
  const { t } = useTranslation();
  const { hotel, fetchHotel } = useFetchHotel();
  const [formValues, setFormValues] = useState(adminDefaultValues);
  const [packageTouched, setPackageTouched] = useState(false);

  useEffect(() => {
    setFormValues(initialValues(hotel));
    setPackageTouched(false);
  }, [hotel]);

  const handleCancel = () => {
    setFormValues(initialValues(hotel));
    setPackageTouched(false);
  };

  function handleInputChange({ target: { name, value } }) {
    var values = { ...formValues, [name]: value };
    setFormValues(values);
  }

  function handleCheckboxChange({ target: { name, checked } }) {
    if (name === "aiUpsellsPackage") {
      setPackageTouched(true);
      setFormValues({ ...formValues, upsellsEnabled: checked, aiEnabled: checked });
    } else {
      setFormValues({ ...formValues, [name]: checked });
    }
  }

  const handleOnSubmit = (e) => {
    e.preventDefault();
    const request = changedVisibleFields(formValues, initialValues(hotel), Object.keys(adminDefaultValues),
      [...(formValues.serviceType === "messaging" ? [...LOCK_FIELDS, "messagingEnabled", "messagingDisabled"] : []),
        ...(!getOfferCapabilities(formValues).messaging ? ["messagingDisabled"] : [])]);
    if (packageTouched) {
      request.upsellsEnabled = formValues.upsellsEnabled;
      request.aiEnabled = formValues.aiEnabled;
    }
    if (!Object.keys(request).length) return;
    HotelService.updateHotel(request).then(
      (response) => {
        if (response.data.ok) {
          presentToastSuccess("Information Updated");
          fetchHotel();
        }
      },
      (error) => {
        presentToastErrorContent(error);
      },
    );
  };

  const handleClearPmsUpdatedDate = async (e) => {
    e.preventDefault();
    HotelService.updateHotel({ pmsLastUpdated: null }).then(
      (response) => {
        if (response.data.ok) {
          presentToastSuccess("PMS last updated date cleared");
          fetchHotel();
        }
      },
      (error) => {
        presentToastErrorContent(error);
      },
    );
  };

  const handleExpectedToOnlineCheckin = (e) => {
    e.preventDefault();
    presentAlertPrompt({
      title: t("settingsAdmin.expectedToOnlineCheckin"),
      message: t("settingsAdmin.expectedToOnlineCheckinConfirm"),
      buttonOKText: t("settingsAdmin.expectedToOnlineCheckinButton"),
      onOK: () =>
        HotelService.expectedToOnlineCheckin().then(
          (response) => {
            presentToastSuccess(
              t("settingsAdmin.expectedToOnlineCheckinDone", {
                count: response.data.updated,
              }),
            );
          },
          (error) => {
            presentToastErrorContent(error);
          },
        ),
    });
  };

  const hidden = name => (formValues.serviceType === "messaging" && [...LOCK_FIELDS, "messagingEnabled", "messagingDisabled"].includes(name)) ||
    (name === "messagingDisabled" && !getOfferCapabilities(formValues).messaging);

  const renderCheckbox = (label, name, disabled) => hidden(name) ? null : (
    <CheckBox
      name={name}
      checked={formValues[name]}
      onChange={handleCheckboxChange}
      label={label}
      disabled={disabled}
    />
  );

  const renderSelect = (label, name, options) => hidden(name) ? null : (
    <FormField label={label} width="medium">
      <Select
        size="medium"
        labelKey="label"
        valueKey={{ key: "value", reduce: true }}
        placeholder={t("common.select")}
        value={formValues[name]}
        options={options}
        onChange={({ value: nextValue }) =>
          setFormValues({ ...formValues, [name]: nextValue,
            ...(name === "serviceType" && !packageTouched && hotel.upsellsEnabled === undefined
              ? { upsellsEnabled: getOfferCapabilities({ ...hotel, serviceType: nextValue }).upsells } : {}),
          })
        }
      />
    </FormField>
  );

  const renderTextInput = (label, name) => hidden(name) ? null : (
    <FormField label={label} width="medium">
      <TextInput
        name={name}
        value={formValues[name] || ""}
        onChange={handleInputChange}
      />
    </FormField>
  );

  return (
    <div>
      <Heading level={3} margin="small">
        Account
      </Heading>

      <Box
        width={{ max: "large", width: "100%" }}
        gap="medium"
        background="light-2"
        round
        pad="medium"
      >
        {renderTextInput("PMS ID", "pmsId")}
        {renderCheckbox("Disabled Account", "disabled")}
        {renderCheckbox("Onboarding in progress (demo)", "demo")}
        {renderCheckbox(
          t("settingsAdmin.pauseCommunications"),
          "disableAllCommunications",
        )}
        <Text size="small">{t("settingsAdmin.pauseCommunicationsHelp")}</Text>
        {renderCheckbox(
          "Whitelabel (use hotel SMTP + branding)",
          "whiteLabeled",
        )}
        {renderTextInput("Renew Subscription", "renewSubscription")}
        {renderTextInput(
          "Subscription Expiration (MM/DD/YYYY)",
          "subscriptionExpiration",
        )}
        {hotel.onlineCheckinEnabled && (
          <Box width="medium">
            <Button
              label={t("settingsAdmin.expectedToOnlineCheckin")}
              onClick={handleExpectedToOnlineCheckin}
            />
          </Box>
        )}
        {pmsThatCanClearPmsUpdated.includes(hotel.pmsSlug) && (
          <Box width="medium">
            <Button
              label={t("settingsAdmin.clearPmsUpdatedDate")}
              onClick={handleClearPmsUpdatedDate}
            />
            <Text size="small" alignSelf="center">
              {hotel.pmsLastUpdated
                ? moment(hotel.pmsLastUpdated).format("LLLL")
                : "Never"}
            </Text>
          </Box>
        )}
      </Box>

      <Heading level={3} margin="small">
        Features
      </Heading>

      <Box
        width={{ max: "large", width: "100%" }}
        gap="medium"
        background="light-2"
        round
        pad="medium"
      >
        {renderSelect("Service", "serviceType", [
          { label: "Access Doors", value: "access-doors" },
          { label: "Messaging", value: "messaging" },
        ])}
        {renderCheckbox(t("offers.messagingPackageTitle"), "messagingEnabled")}
        <Text size="small">{t(formValues.serviceType === "messaging" ? "settingsAdmin.messagingIncluded" : "settingsAdmin.messagingEntitlementHelp")}</Text>
        {renderCheckbox(t("settingsAdmin.pauseGuestChat"), "messagingDisabled")}
        {!hidden("messagingDisabled") && <Text size="small">{t("settingsAdmin.pauseGuestChatHelp")}</Text>}
        {(
          <>
            <CheckBox
              name="aiUpsellsPackage"
              label={t("offers.packageTitle")}
              checked={formValues.upsellsEnabled && formValues.aiEnabled}
              indeterminate={formValues.upsellsEnabled !== formValues.aiEnabled}
              onChange={handleCheckboxChange}
            />
            <Text size="small">{t("settingsAdmin.packageFlagsHelp")}</Text>
            <Text size="small">{t("settingsAdmin.packageFlagsStatus", { ai: t(formValues.aiEnabled ? "settingsAdmin.flagOn" : "settingsAdmin.flagOff"), upsells: t(formValues.upsellsEnabled ? "settingsAdmin.flagOn" : "settingsAdmin.flagOff") })}</Text>
            {formValues.upsellsEnabled !== formValues.aiEnabled && (
              <Text size="small">{t("offers.mixedFlags")}</Text>
            )}
          </>
        )}
        {renderSelect("Language", "language", LANGUAGE_OPTIONS_LIST)}
        {renderSelect(
          "Pronouns (French)",
          "pronounsFr",
          PRONOUNS_FR_OPTIONS_LIST,
        )}
        {renderSelect("Lock type", "lockType", LOCK_TYPE_OPTIONS_LIST)}
        {formValues.lockType === "ttlock" &&
          renderCheckbox(
            "Housekeeping (track room clean/dirty status from TTLock unlock records)",
            "houseCleaningEnabled",
          )}

        {renderCheckbox("Send Mobile Key Quietly", "sendMobileKeyQuietly")}
        {renderCheckbox("Use Upgrade Modules", "useUpgradeModules")}
        {renderCheckbox("Disable Battery Check", "disableBatteryCheck")}
        {renderCheckbox("AI Insights Enabled (AI Brief)", "aiInsightsEnabled")}
        {renderCheckbox("Has Wifi Gateway", "hasWifiGateway")}
        {renderCheckbox("Use Passcodes", "usePasscodes")}
        {renderTextInput(
          "Number Of Last Digits Used",
          "numberOfLastDigitsUsed",
        )}
        {renderCheckbox(
          "Opt Out Marketing Option Enabled",
          "optOutMarketingOptionEnabled",
        )}
        {renderCheckbox("Partial Setup", "partialSetup")}
        {renderCheckbox("Partial Setup Strict", "partialSetupStrict")}
        {renderTextInput("Admin Pin (Master cards encoding)", "adminPin")}
        {renderTextInput("Encoder Sector", "sector")}
        {renderTextInput(
          `Common Area Fixed Label (en=[x] fr=[x])`,
          "commonAreaFixedLabel",
        )}
        {renderTextInput(
          "Common Area Fixed Access Code",
          "commonAreaFixedPasscode",
        )}
      </Box>
      <Box direction="row" justify="between" margin={{ top: "medium" }}>
        <Button label={t("common.cancel")} onClick={handleCancel} />
        <Button
          type="submit"
          label={t("common.update")}
          primary
          onClick={handleOnSubmit}
        />
      </Box>
    </div>
  );
}
